import { useEffect, useRef, useState } from "react";
import { type useTranslate } from "@refinedev/core";
import { Button, Modal } from "@fawkes/ui";

import { fetchPrintCss } from "../commMediaApi";
import { importTaak, useImportTaak, verstreken, type ImportBron } from "./importTaak";
import { RawPrintCanvas } from "./RawPrintCanvas";
import "./importTaak.css";

// communicatie: een bestaand document overnemen als sjabloon. Je uploadt de brief die je nu met de hand
// invult — een pdf of een scan — en de api leest hem, bouwt hem na als html en zet op de plekken waar
// een gegeven hoort het bijbehorende veld uit de catalogus neer.
//
// Dezelfde grens als de AI-assistent hiernaast: er wordt niets opgeslagen en niets automatisch
// toegepast. Je ziet het voorstel met de herkende velden erbij, en pas als je op Overnemen klikt komt
// het in de editor. De api houdt bovendien elk veld tegen dat niet in de catalogus staat, dus er kan
// geen verzonnen placeholder in een sjabloon belanden.
//
// Het nabouwen zelf is een taak van de app (importTaak), niet van dit venster: het duurt minuten, en
// je mag het venster sluiten en verder werken. Dit venster laat alleen zien hoe het ervoor staat.

type Translate = ReturnType<typeof useTranslate>;

/**
 * Wat de kiezer accepteert. Een pdf of scan gaat als afbeelding naar het model — daar ís de opmaak
 * het document. Uit een Word-bestand halen we tekst en tabellen; lettertypes, kleuren en
 * afbeeldingen blijven dan achter, en dat staat ook in het scherm.
 *
 * Het oude binaire .doc staat er bewust niet bij: dat kan niet gelezen worden, en een keuze die
 * altijd op een foutmelding uitloopt hoor je niet aan te bieden.
 */
const SOORTEN = ".pdf,.docx,.png,.jpg,.jpeg,.webp";

/** De teller die laat zien dat er echt iets gebeurt; zonder beweging denkt iedereen dat hij hangt. */
function Teller({ gestart }: { gestart: number }) {
  const [nu, setNu] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNu(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return <span className="comm-import__teller">{verstreken(gestart, nu)}</span>;
}

export function DocumentImportDialog({
  open,
  onClose,
  onOvernemen,
  bron,
  t,
}: {
  open: boolean;
  onClose: () => void;
  /** Wordt aangeroepen met de nagebouwde html zodra je hem overneemt. */
  onOvernemen: (bodyHtml: string) => void;
  /** Het sjabloon dat openstaat; daar brengt de melding je terug als het klaar is. */
  bron: ImportBron;
  t: Translate;
}) {
  const bestandRef = useRef<HTMLInputElement>(null);
  const taak = useImportTaak();

  useEffect(() => {
    importTaak.setDialoogOpen(open);
    return () => importTaak.setDialoogOpen(false);
  }, [open]);

  // De afdrukstijl voor het voorbeeld; pas nodig als er iets te tonen is.
  const [printCss, setPrintCss] = useState<string | null>(null);
  const heeftVoorstel = taak.status === "klaar";
  useEffect(() => {
    if (!heeftVoorstel || printCss != null) return;
    let actief = true;
    fetchPrintCss()
      .then((css) => { if (actief) setPrintCss(css); })
      .catch(() => { if (actief) setPrintCss(""); });
    return () => { actief = false; };
  }, [heeftVoorstel, printCss]);

  const bezig = taak.status === "bezig";
  const voorstel = taak.status === "klaar" ? taak.voorstel : null;
  const fout = taak.status !== "fout" ? null
    : taak.melding
      ?? (taak.code === 401 || taak.code === 403 ? t("communicatieImport.fout.sessie")
        : taak.code ? t("communicatieImport.fout.status", { args: [String(taak.code)] })
          : t("communicatieImport.fout.generiek"));
  const naam = taak.status === "leeg" ? null : taak.bestandsnaam;
  // Een resultaat voor een ander sjabloon mag hier ook worden overgenomen, maar dat moet je dan wel zien.
  const anderSjabloon = taak.status !== "leeg" && taak.templateId !== bron.templateId ? taak.templateNaam : null;

  /** Venster dicht. Loopt het nog, dan loopt het door; een uitkomst die je wegklikt, vervalt. */
  function sluit() {
    if (!bezig) importTaak.stop();
    onClose();
  }

  return (
    <Modal
      open={open}
      title={t("communicatieImport.titel")}
      size="wide"
      onClose={bezig ? onClose : sluit}
      footer={
        bezig ? (
          <>
            <Button variant="secondary" onClick={() => importTaak.stop()}>
              {t("communicatieImport.stoppen")}
            </Button>
            <Button variant="primary" onClick={onClose}>
              {t("communicatieImport.verderWerken")}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={sluit}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              disabled={!voorstel?.bodyHtml}
              onClick={() => {
                if (voorstel?.bodyHtml) onOvernemen(voorstel.bodyHtml);
                sluit();
              }}
            >
              {t("communicatieImport.overnemen")}
            </Button>
          </>
        )
      }
    >
      <p className="comm-import__intro">{t("communicatieImport.intro")}</p>
      {/* Welk bestand je het beste kunt geven, vóór je kiest — niet achteraf als je brief er
          anders uitziet dan je hem kent. */}
      <p className="comm-import__formaten">{t("communicatieImport.formaten")}</p>

      <div className="comm-import__kiezer">
        <input
          ref={bestandRef}
          type="file"
          accept={SOORTEN}
          className="comm-import__invoer"
          onChange={(e) => {
            const bestand = e.target.files?.[0];
            if (bestand) void importTaak.start(bestand, bron);
            e.target.value = "";
          }}
        />
        <Button variant="secondary" onClick={() => bestandRef.current?.click()} disabled={bezig}>
          {t("communicatieImport.kies")}
        </Button>
        {naam && <span className="comm-import__bestandsnaam">{naam}</span>}
      </div>

      {anderSjabloon && (
        <div className="comm-editor__alert comm-editor__alert--conflict" role="status">
          {t("communicatieImport.anderSjabloon", { args: [anderSjabloon] })}
        </div>
      )}

      {taak.status === "bezig" && (
        <div className="comm-import__bezig" role="status" aria-live="polite">
          <span className="comm-import__spinner" aria-hidden="true" />
          <div>
            <strong>
              {t("communicatieImport.bezig")} <Teller gestart={taak.gestart} />
            </strong>
            <p>{t("communicatieImport.bezigUitleg")}</p>
          </div>
        </div>
      )}

      {fout && (
        <div className="comm-editor__alert comm-editor__alert--error" role="alert">
          {fout}
          {/* Wat de server onderweg zag, uitklapbaar. Dit scherm werkt bij de ene klant wel en bij de
              andere niet, en dan heb je aan "het is mislukt" niets: hier staat wat er binnenkwam, hoe
              de pagina's gerenderd zijn (inkt=0% betekent een leeg vel) en wat het model terugstuurde. */}
          {taak.status === "fout" && taak.diagnose && (
            <details className="comm-import__diagnose">
              <summary>{t("communicatieImport.diagnose")}</summary>
              <code>{taak.diagnose}</code>
            </details>
          )}
        </div>
      )}

      {voorstel?.bodyHtml && (
        <div className="comm-import__uitkomst">
          {/* Links wat eruit komt, rechts waarop het model gegokt heeft. Zonder die tweede kolom kun je
              niet beoordelen of "Fam. van Diessen" terecht een veld is geworden. */}
          <div className="comm-import__voorbeeld">
            <h4>{t("communicatieImport.voorbeeld")}</h4>
            {/* Het voorbeeld met de afdrukopmaak, zoals de editor en de pdf het tonen. Als losse html
                stond de voettekst bovenaan: die hoort met position:fixed onderaan elke pagina, en dat
                doet alleen de afdrukstijl. De api heeft de html al gewassen. */}
            <div className="comm-import__vel">
              {printCss == null ? (
                <p className="comm-import__leeg">{t("common.loading")}</p>
              ) : (
                <RawPrintCanvas
                  bodyHtml={voorstel.bodyHtml}
                  printCss={printCss}
                  onChange={() => {}}
                  readOnly
                  ariaLabel={t("communicatieImport.voorbeeld")}
                />
              )}
            </div>
          </div>

          <div className="comm-import__velden">
            <h4>
              {t("communicatieImport.velden", { args: [String(voorstel.velden.length)] })}
            </h4>
            {voorstel.velden.length === 0 ? (
              <p className="comm-import__leeg">{t("communicatieImport.geenVelden")}</p>
            ) : (
              <ul className="comm-import__veldlijst">
                {voorstel.velden.map((veld) => (
                  <li key={veld.token}>
                    <code>{`{${veld.token}}`}</code>
                    <span className="comm-import__veldlabel">{veld.label}</span>
                    {veld.gevondenTekst && (
                      <span className="comm-import__gevonden">{veld.gevondenTekst}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {voorstel.herhaalblokken.length > 0 && (
              <div className="comm-import__blokken">
                <h4>
                  {t("communicatieImport.herhaalblokken", {
                    args: [String(voorstel.herhaalblokken.length)],
                  })}
                </h4>
                <ul className="comm-import__bloklijst">
                  {voorstel.herhaalblokken.map((blok) => (
                    <li key={blok}>
                      <code>{blok}</code>
                      <span className="comm-import__veldlabel">
                        {t("communicatieImport.herhaalblok.uitleg", { args: [blok] })}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {voorstel.onbekend.length > 0 && (
              <div className="comm-editor__alert comm-editor__alert--conflict" role="status">
                {t("communicatieImport.onbekend", { args: [voorstel.onbekend.join(", ")] })}
              </div>
            )}

            <p className="comm-import__nazien">{t("communicatieImport.nazien")}</p>
          </div>
        </div>
      )}
    </Modal>
  );
}
